/**
 * Auth error handling and on-brand copy.
 *
 * Cognito surfaces raw, machine-shaped messages ("Password did not conform with
 * policy: ...", "Invalid verification code provided."). We wrap every rejection
 * from the auth layer in {@link AuthError} so the original Cognito `code` (e.g.
 * `UsernameExistsException`) survives, then map it to friendly, on-brand copy in
 * one place so every screen (the inline gate and the standalone forms) reads the
 * same voice.
 */

/** An auth-layer error that preserves the originating Cognito error code. */
export class AuthError extends Error {
  readonly code: string | undefined;
  constructor(message: string, code?: string) {
    super(message);
    this.name = 'AuthError';
    this.code = code;
  }
}

/** The password requirements, mirrored from the Cognito pool policy (see infra/lib/auth-stack.ts). */
export const PASSWORD_HINT =
  'At least 12 characters, with an uppercase letter, a lowercase letter, a number, and a symbol.';

/**
 * Pull a Cognito error code from an unknown error shape. Handles our own
 * {@link AuthError}, the raw `{ code }` object Cognito hands its callbacks, and
 * a `name` that looks like a Cognito exception.
 */
export function cognitoCode(error: unknown): string | undefined {
  if (error instanceof AuthError) {
    return error.code;
  }
  if (typeof error === 'object' && error !== null) {
    const code = (error as { code?: unknown }).code;
    if (typeof code === 'string' && code.length > 0) {
      return code;
    }
    const name = (error as { name?: unknown }).name;
    if (typeof name === 'string' && name.endsWith('Exception')) {
      return name;
    }
  }
  return undefined;
}

function rawMessage(error: unknown): string {
  return error instanceof Error ? error.message : '';
}

/** True when the error means the email is already registered. */
export function isUsernameExists(error: unknown): boolean {
  return cognitoCode(error) === 'UsernameExistsException' || /already exists/i.test(rawMessage(error));
}

const ALREADY_HAVE_ACCOUNT = 'You already have an account. Sign in instead.';
const BAD_CODE = 'That code did not match. Check the email and try again, or resend.';
const WEAK_PASSWORD = `That password is too weak. ${PASSWORD_HINT}`;
const TOO_MANY_ATTEMPTS = 'Too many attempts. Please wait a little while, then try again.';

/**
 * Map an auth error to friendly, on-brand copy. Keys off the Cognito code first,
 * then falls back to matching the raw message (so it also handles errors that
 * arrive as a plain Error without a code, e.g. from a stubbed auth layer).
 */
export function friendlyAuthMessage(
  error: unknown,
  fallback = 'Something went wrong. Please try again.',
): string {
  const code = cognitoCode(error);
  switch (code) {
    case 'UsernameExistsException':
      return ALREADY_HAVE_ACCOUNT;
    // `UserNotFoundException` is folded in here on purpose: a reset code for an address
    // with no account looks, to the user, exactly like a bad code. Mapping it to the same
    // neutral copy keeps the reset flow from revealing whether an account exists.
    case 'CodeMismatchException':
    case 'ExpiredCodeException':
    case 'UserNotFoundException':
      return BAD_CODE;
    case 'InvalidPasswordException':
      return WEAK_PASSWORD;
    case 'LimitExceededException':
    case 'TooManyRequestsException':
      return TOO_MANY_ATTEMPTS;
    default:
      break;
  }
  const raw = rawMessage(error);
  if (/already exists/i.test(raw)) {
    return ALREADY_HAVE_ACCOUNT;
  }
  if (/invalid verification code/i.test(raw) || /mismatch/i.test(raw)) {
    return BAD_CODE;
  }
  if (/password did not conform|password.*policy|invalidpassword/i.test(raw)) {
    return WEAK_PASSWORD;
  }
  if (/attempt limit exceeded|too many/i.test(raw)) {
    return TOO_MANY_ATTEMPTS;
  }
  return raw.length > 0 ? raw : fallback;
}
